import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { useParams } from 'react-router-dom';
import { ClipboardCheck, Check, X, Mail, MessageSquare, Megaphone, AlertCircle, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Empty } from '@/components/Empty';
import { cn } from '@/lib/cn';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const TYPE_META: Record<string, { label: string; Icon: typeof Mail }> = {
  email_reply: { label: 'Email', Icon: Mail },
  social_caption: { label: 'Post social', Icon: MessageSquare },
  social_caption_sensitive: { label: 'Post sensible', Icon: AlertCircle },
  dm_prospect: { label: 'DM prospect', Icon: MessageSquare },
  dm_response: { label: 'DM réponse', Icon: MessageSquare },
  amazon_ad_copy: { label: 'Pub Amazon', Icon: Megaphone },
  monthly_report_nebine: { label: 'Rapport mensuel', Icon: Mail },
};

export function ApprovalsPage() {
  const { slug } = useParams<{ slug: string }>();
  const { getToken } = useAuth();
  const qc = useQueryClient();

  const approvalsQuery = useQuery({
    queryKey: ['approvals', slug],
    queryFn: () => api.listApprovals(slug!, () => getToken()),
    refetchInterval: 30_000,
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.approveApproval(slug!, id, () => getToken()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approvals', slug] });
      qc.invalidateQueries({ queryKey: ['kpis', slug] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.rejectApproval(slug!, id, reason, () => getToken()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approvals', slug] });
      qc.invalidateQueries({ queryKey: ['kpis', slug] });
    },
  });

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold leading-tight">Validations en attente</h1>
        <p className="text-sm text-ducens-muted mt-1">
          Outputs des agents IA à valider avant envoi au monde extérieur.
        </p>
      </div>

      {approvalsQuery.isPending && (
        <div className="flex items-center gap-2 text-ducens-muted">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement...
        </div>
      )}

      {approvalsQuery.data && approvalsQuery.data.length === 0 && (
        <Empty
          Icon={ClipboardCheck}
          title="Tout est à jour 🎉"
          description="Aucun output en attente de validation. Les agents tournent tranquillement."
        />
      )}

      {approvalsQuery.data && approvalsQuery.data.length > 0 && (
        <div className="space-y-3">
          {approvalsQuery.data.map((approval) => {
            const meta = TYPE_META[approval.output_type] ?? { label: approval.output_type, Icon: Mail };
            return (
              <ApprovalCard
                key={approval.id}
                approval={approval}
                meta={meta}
                onApprove={() => approveMutation.mutate(approval.id)}
                onReject={(reason) => rejectMutation.mutate({ id: approval.id, reason })}
                isApproving={approveMutation.isPending}
                isRejecting={rejectMutation.isPending}
              />
            );
          })}
        </div>
      )}
    </>
  );
}

type Approval = Awaited<ReturnType<typeof api.listApprovals>>[number];

function ApprovalCard({
  approval,
  meta,
  onApprove,
  onReject,
  isApproving,
  isRejecting,
}: {
  approval: Approval;
  meta: { label: string; Icon: typeof Mail };
  onApprove: () => void;
  onReject: (reason: string) => void;
  isApproving: boolean;
  isRejecting: boolean;
}) {
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const { Icon } = meta;

  return (
    <div className="bg-white border border-ducens-border rounded-lg p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-ducens-muted" />
          <div>
            <div className="text-sm font-medium">{meta.label}</div>
            <div className="text-xs text-ducens-muted">
              {approval.agent_name} ·{' '}
              {formatDistanceToNow(new Date(approval.created_at), { locale: fr, addSuffix: true })}
              {approval.recipient && <> · pour {approval.recipient}</>}
            </div>
          </div>
        </div>
      </div>

      <pre className="bg-ducens-bg p-3 rounded-md text-sm whitespace-pre-wrap font-sans border border-ducens-border max-h-72 overflow-y-auto">
        {approval.content}
      </pre>

      {showReject ? (
        <div className="mt-3 space-y-2">
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Raison du rejet (sera utilisée pour améliorer l'agent)..."
            className="w-full text-sm border border-ducens-border rounded-md p-2 resize-none focus:outline-none focus:border-ducens-ink"
            rows={2}
          />
          <div className="flex gap-2">
            <button
              onClick={() => {
                onReject(rejectReason);
                setShowReject(false);
                setRejectReason('');
              }}
              disabled={!rejectReason || isRejecting}
              className="px-3 py-1.5 rounded-md bg-ducens-danger text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              Confirmer le rejet
            </button>
            <button
              onClick={() => setShowReject(false)}
              className="px-3 py-1.5 rounded-md border border-ducens-border text-sm hover:bg-ducens-bg"
            >
              Annuler
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2 mt-3">
          <button
            onClick={onApprove}
            disabled={isApproving}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-ducens-ink text-white text-sm font-medium',
              'hover:opacity-90 disabled:opacity-50'
            )}
          >
            <Check className="w-4 h-4" /> Approuver
          </button>
          <button
            onClick={() => setShowReject(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-ducens-border text-sm hover:bg-ducens-bg"
          >
            <X className="w-4 h-4" /> Rejeter
          </button>
        </div>
      )}
    </div>
  );
}
